/**
 * Schémas Zod — rapports de comparaison (chunk 9.AI.1).
 *
 * Source DB : `app.comparison_reports` (migration 0072).
 *
 * ## Ce que la table contient, et ce qu'elle ne contient pas
 *
 * Des **RÉFÉRENCES** : quel dossier, quelles propositions, quelles extractions,
 * quand, par qui, en quelle langue, pour quelle audience. **Aucune valeur
 * comparée n'est copiée.** Le contenu se reconstruit à la demande depuis la même
 * lecture que l'écran comparateur, pour que les deux ne puissent pas raconter
 * deux histoires.
 *
 * ## `confirmed_at` : brouillon vs confirmé, et rien d'autre
 *
 *   `confirmed_at IS NULL`      → brouillon, non partageable
 *   `confirmed_at IS NOT NULL`  → confirmé par un conseiller nommé
 *
 * **Confirmé ne veut PAS dire figé.** Une extraction rejouée, une correction
 * humaine ou une donnée corrigée après la confirmation *changera* ce qu'une
 * reconstruction ultérieure affiche. C'est le prix assumé de l'invariant « une
 * seule source de vérité », et c'est pourquoi `sources_changed` existe : le
 * rapport dit lui-même quand ses références ne décrivent plus ce qu'il montre.
 * Un archivage opposable serait un autre produit — copie immuable, rétention
 * propre, décision séparée.
 *
 * ## Portée : le dossier entier, jamais une demande
 *
 * Le comparateur sait filtrer par `quote_request_id` ; le rapport ne le fait
 * pas. Un rapport porte sur le dossier — c'est ce qu'un client reçoit — et
 * figer un filtre dans une trace dont les valeurs continuent de bouger
 * produirait un document dont ni le périmètre ni le contenu ne seraient
 * vérifiables.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

/** Les deux niveaux de présentation. Même calcul, pas deux moteurs. */
export const COMPARISON_REPORT_AUDIENCES = ["advisor", "client"] as const;
export const ComparisonReportAudienceSchema = z.enum(COMPARISON_REPORT_AUDIENCES);
export type ComparisonReportAudience = z.infer<typeof ComparisonReportAudienceSchema>;

/** FR/EN strict — parité `language_chk` de la migration 0072. */
export const COMPARISON_REPORT_LANGUAGES = ["fr", "en"] as const;
export const ComparisonReportLanguageSchema = z.enum(COMPARISON_REPORT_LANGUAGES);
export type ComparisonReportLanguage = z.infer<typeof ComparisonReportLanguageSchema>;

// -----------------------------------------------------------------------------
// Row (lecture wire-side)
// -----------------------------------------------------------------------------
//
// Anti-leak : `tenant_id` reste implicite (RLS), `deleted_at` reste interne, et
// les auteurs sortent en `display_name` joint — jamais en UUID brut, parité
// `ComparisonNoteRowSchema`.
//
// `submission_ids` / `extraction_ids` sont exposés parce que ce routeur est
// humain-only et sert le conseiller : ce sont SES références de traçabilité.
// La projection CLIENT, elle, n'en porte aucune — c'est `projectReport()` qui
// garantit ça, et un test l'y tient.

export const ComparisonReportRowSchema = z.object({
  id: UuidSchema,
  project_id: UuidSchema,
  audience: ComparisonReportAudienceSchema,
  language: ComparisonReportLanguageSchema,
  submission_ids: z.array(UuidSchema),
  extraction_ids: z.array(UuidSchema),
  /** Au moins une cellule retenue portait une correction humaine. */
  had_human_overrides: z.boolean(),
  /** Au moins une cellule demandait encore une validation. Avertit avant partage. */
  had_needs_review: z.boolean(),
  generated_by_display_name: z.string().nullable(),
  generated_at: z.string(),
  /** NULL = brouillon. Non-NULL = confirmé, et irréversible. */
  confirmed_at: z.string().nullable(),
  confirmed_by_display_name: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ComparisonReportRow = z.infer<typeof ComparisonReportRowSchema>;

// -----------------------------------------------------------------------------
// Create — POST /v1/comparison-reports
// -----------------------------------------------------------------------------
//
// Générer est idempotent tant que le brouillon vit : un conseiller qui régénère
// trois fois avant de confirmer ne laisse pas trois lignes. La garantie vient de
// l'index partiel `uq_comparison_reports_live_draft`, pas d'une vérification
// applicative — leçon reconduite depuis 9.AA.1a.

export const CreateComparisonReportSchema = z.object({
  project_id: UuidSchema,
  audience: ComparisonReportAudienceSchema,
  language: ComparisonReportLanguageSchema,
});
export type CreateComparisonReportInput = z.infer<typeof CreateComparisonReportSchema>;

// -----------------------------------------------------------------------------
// List query — GET /v1/projects/:id/comparison-reports
// -----------------------------------------------------------------------------
//
// Sans ça, l'interface qui recharge après une confirmation n'aurait qu'un seul
// geste possible — régénérer — et créerait un brouillon là où le conseiller
// voulait relire ce qu'il a confirmé.

export const ComparisonReportsListQuerySchema = z.object({
  audience: ComparisonReportAudienceSchema.optional(),
  language: ComparisonReportLanguageSchema.optional(),
});
export type ComparisonReportsListQuery = z.infer<typeof ComparisonReportsListQuerySchema>;
